// frontend/utils/documentUtils.ts

/** 프론트엔드 전역에서 사용하는 사용자 세션 타입 */
export interface UserSession {
  id: string;
  name: string;
  role: string;           // "ADMIN" | "USER" | "GUEST"
  department?: string;
  isDeptHead?: boolean;
  /** NextAuth auth 콜백에서 주입되는 Bearer JWT. API 요청의 Authorization 헤더로 사용 */
  accessToken?: string;
}

/**
 * 문서 활동 로그 항목 타입.
 * UpdatesAnalyticsPanel, VersionHistoryPanel에서 공유합니다.
 */
export interface DocumentLog {
  id: string;
  userName: string;
  actionType: string;
  createdAt: string;
  addedBlocks?: string[];
  hasMoreBlocks?: boolean;
  previewUnavailableReason?: string;
}

/**
 * 현재 사용자가 특정 문서를 편집할 수 있는지 판별합니다. (EDIT 이상)
 *
 * - ADMIN: 팀 문서 조회만 가능, 편집 불가
 * - GUEST: 모든 편집 불가
 * - USER: 본인 소유의 개인 문서 / 개인·부서 권한(permLevel)이 EDIT 이상인 팀 문서에서 편집 가능
 *
 * 서버가 permLevel을 내려주면 그 값을 우선 신뢰하고, 없는(레거시) 응답에 한해
 * accessDept 기반 휴리스틱으로 폴백합니다.
 *
 * 사이드바(checkSidebarEditable)와 문서 페이지 모두 동일한 규칙을 사용하므로
 * 이 함수 하나로 통합합니다.
 */
export const checkEditable = (docData: any, currentUser: UserSession): boolean => {
  if (!currentUser || currentUser.role === "ADMIN" || currentUser.role === "GUEST") return false;
  if (docData.workspaceType === "PERSONAL" && String(docData.ownerId) !== String(currentUser.id)) return false;

  if (docData.permLevel !== undefined && docData.permLevel !== null) {
    return docData.workspaceType === "PERSONAL" || ["EDIT", "FULL"].includes(docData.permLevel);
  }

  if (docData.workspaceType === "TEAM") {
    const allowedDepts = docData.accessDept?.split(",").map((d: string) => d.trim()) || [];
    if (!allowedDepts.includes(currentUser.department || "")) return false;
  }
  return true;
};

/**
 * 현재 사용자가 문서에 대해 전체 허용(FULL) 권한을 가지는지 판별합니다.
 * 옮기기·삭제·권한 설정 등 민감한 작업의 접근 제어에 사용됩니다.
 * (구 이름: checkIsTrueOwner — "최초 작성팀/소유자"가 아니라 FULL 권한 보유 여부를 의미)
 */
export const checkIsTrueOwner = (docData: any, currentUser: UserSession): boolean => {
  if (!currentUser) return false;
  if (docData.workspaceType === "PERSONAL") return String(docData.ownerId) === String(currentUser.id);

  if (docData.permLevel !== undefined && docData.permLevel !== null) {
    return docData.permLevel === "FULL";
  }

  const primaryDept =
    docData.workspaceType === "TEAM" ? docData.accessDept?.split(",")[0]?.trim() : null;
  return primaryDept === currentUser.department;
};

/**
 * 영구 삭제되거나 접근 불가한 문서 ID를 localStorage 블랙리스트에 등록합니다.
 * PageLinkBlock이 이 목록을 확인하여 삭제된 문서로의 링크를 비활성화합니다.
 * 최대 50개를 FIFO 방식으로 유지합니다.
 */
export const addToDeadLinks = (deadId: string): void => {
  let deadLinks: string[] = [];
  try { deadLinks = JSON.parse(localStorage.getItem("alpha_dead_links") || "[]"); } catch { /* ignore */ }
  if (!deadLinks.includes(deadId)) {
    deadLinks.push(deadId);
    if (deadLinks.length > 50) deadLinks.shift();
    try { localStorage.setItem("alpha_dead_links", JSON.stringify(deadLinks)); } catch { /* ignore */ }
  }
};

/**
 * 신규 문서 ID를 생성합니다. crypto.randomUUID를 우선 사용하고,
 * 지원하지 않는 환경(구형 브라우저 등)에서는 타임스탬프 기반 ID로 폴백합니다.
 */
export const generateDocId = (): string =>
  typeof window !== "undefined" && window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `doc-${Date.now()}`;
